import { useEffect, useState } from 'react';

// One-time, dismissible "install this app" nudge shown only on a mobile browser tab
// (never when already running as an installed PWA). On Android/Chromium we capture the
// browser's `beforeinstallprompt` and offer a real Install button; on iOS Safari (no such
// event) we show static Add-to-Home-Screen guidance. Dismissal is persisted so it shows
// at most once. Mounted from App.tsx behind `isMobile`; this component owns the rest.

const DISMISS_KEY = 'terum-a2hs-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // iOS Safari exposes standalone on navigator, not via matchMedia.
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return displayStandalone || iosStandalone;
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

// On iOS, "Add to Home Screen" only exists in Safari itself. Chrome (CriOS), Firefox (FxiOS),
// Edge (EdgiOS), Opera (OPiOS) and most in-app webviews (e.g. opening the link from Slack)
// have no Share → Add to Home Screen, so the generic guidance would silently do nothing.
// Detect that case so we can tell the user to reopen the page in Safari.
function isIOSNonSafari(): boolean {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  // A dedicated non-Safari iOS browser advertises its own engine token.
  if (/crios|fxios|edgios|opios|mercury/i.test(ua)) return true;
  // Real mobile Safari carries BOTH "Safari" and "Version/"; an in-app WKWebView usually lacks one.
  return !(/safari/i.test(ua) && /version\//i.test(ua));
}

export function AddToHomeScreenHint() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone()) return; // already installed — nothing to nudge
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      // localStorage can throw (private mode) — treat as not-dismissed.
    }
    if (dismissed) return;

    // Android/Chromium: the browser fires this when the app is installable.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // keep our own UI in control of when to prompt
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — surface static guidance shortly after load
    // so it doesn't compete with the first paint.
    let timer: number | undefined;
    if (isIOS()) {
      timer = window.setTimeout(() => setVisible(true), 1200);
    }

    // If the app gets installed while this tab is open, retire the nudge.
    const onInstalled = () => rememberDismissed();
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const rememberDismissed = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore — worst case the hint reappears next visit
    }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {
      // user closed the native prompt or it errored — either way we're done nudging
    }
    rememberDismissed();
  };

  if (!visible) return null;

  // With no `deferred` event, `visible` was set by the iOS timer — so we're on iOS and only
  // need to split Safari (real install steps) from non-Safari (must reopen in Safari first).
  const iosNonSafari = isIOSNonSafari();

  return (
    <div className="a2hs-banner" role="region" aria-label="Install Terum">
      <span className="a2hs-ic" aria-hidden="true">📲</span>
      <span className="a2hs-text">
        {deferred ? (
          // Android/Chromium: the browser is ready to install — offer the real button below.
          <>Install Terum for a full-screen app that opens to your briefing.</>
        ) : iosNonSafari ? (
          // iOS Chrome/Firefox/Edge/in-app browser: Add to Home Screen isn't available here.
          <>Open this page in <strong>Safari</strong> to install Terum, then tap Share → Add to Home Screen.</>
        ) : (
          // iOS Safari: the manual Share → Add to Home Screen flow works.
          <>Tap Share, then <strong>Add to Home Screen</strong>, to install Terum.</>
        )}
      </span>
      {deferred && (
        <button className="a2hs-install" onClick={install}>Install</button>
      )}
      <button className="a2hs-close" onClick={rememberDismissed} aria-label="Dismiss">×</button>
    </div>
  );
}
